import {useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import GigsStore from '@/stores/gigsStore';
import useAvailableGigsQuery from '@/hooks/queries/gigs/use-available-gigs-query';
import useGigsCountQuery from '@/hooks/queries/gigs/use-gigs-count-query';

/**
 * Hook that integrates the Zustand gigs store with URL search parameters and react-query
 */
function useGigsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const gigs = GigsStore.useGigs();
  const filters = GigsStore.useFilters();
  const draftFilters = GigsStore.useDraftFilters();
  const currentQueryParams = GigsStore.useCurrentQueryParams();
  const filterResultsCount = GigsStore.useFilterResultsCount();
  const draftFilterResultsCount = GigsStore.useDraftFilterResultsCount();
  const showFiltersModal = GigsStore.useShowFiltersModal();

  const {
    setGigs,
    setFilterResultsCount,
    setDraftFilterResultsCount,
    updateFilter,
    applyFilters: storeApplyFilters,
    resetFilters: storeResetFilters,
    openFiltersModal,
    closeFiltersModal,
    initializeFromUrl,
  } = GigsStore.gigsActions;

  useEffect(() => {
    initializeFromUrl(searchParams);
  }, [initializeFromUrl, searchParams]);

  const draftQueryParams = useMemo(
    () => GigsStore.buildQueryParams(draftFilters),
    [draftFilters]
  );

  const {
    gigs: queriedGigs = [],
    isLoading: queryLoading,
    error: queryError,
    count: queryCount,
    refetch: refetchGigs,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAvailableGigsQuery({params: currentQueryParams});

  useEffect(() => {
    if (!queryLoading && !isFetchingNextPage) {
      setFilterResultsCount(queryCount);
      setGigs(queriedGigs);
    }
  }, [
    queryLoading,
    isFetchingNextPage,
    queryCount,
    queriedGigs,
    setFilterResultsCount,
    setGigs,
  ]);

  let normalizedError: Error | null = null;
  if (queryError) {
    if (queryError instanceof Error) {
      normalizedError = queryError;
    } else {
      normalizedError = new Error(String(queryError));
    }
  }

  const {data: draftCount, isLoading: isDraftCountLoading} = useGigsCountQuery({
    params: draftQueryParams,
    enabled: showFiltersModal,
  });

  useEffect(() => {
    if (draftCount !== null) {
      setDraftFilterResultsCount(draftCount);
    }
  }, [draftCount, setDraftFilterResultsCount]);

  const applyFilters = (): boolean => storeApplyFilters(setSearchParams);
  const resetFilters = (): boolean => storeResetFilters(setSearchParams);

  const gigsFiltersObject = {
    gigs,
    error: normalizedError,
    isLoading: !showFiltersModal && queryLoading,
    filters,
    filterResultsCount,
    draftFilters,
    draftFilterResultsCount,
    isCountLoading: showFiltersModal && isDraftCountLoading,
    showFiltersModal,

    fetchNextPage,
    hasNextPage,
    isFetchingNextPage: !showFiltersModal && isFetchingNextPage,

    updateFilter,
    applyFilters,
    resetFilters,
    openFiltersModal,
    closeFiltersModal,
    refetchGigs,
  };

  return gigsFiltersObject;
}

export default useGigsFilters;
